import {
	Component,
	Ref,
	Vue,
	toNative,
} from 'vue-facing-decorator';
import { saveAs } from 'file-saver';
import * as DB from 'interfaces/database';
import { AddCategoryModel, TabEvent, ImportCatalogModel } from 'interfaces/app';
import { PickerOptions, PickerResponse } from 'filestack-js';
import { BModal } from 'bootstrap-vue';
import Swal from 'sweetalert2';
import mitt from 'mitt';
import 'vue-loading-overlay/dist/css/index.css';
import { CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator } from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import CustomSelect from 'components/CustomSelect';
import CustomFileInput from 'components/CustomFileInput';
import ThemeCategoryService from 'services/ThemeCategoryService';
import OfferingService from 'services/OfferingService';
import LanguageService from 'services/LanguageService';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.ThemeCategoryModel>>();
@Component({
	components: {
		TabulatorBtn,
		CustomSelect,
		CustomFileInput,
	},
	emits: ['openInNewTab', 'routeCategory'],
	mixins: [Template],
})
class CategoryTable extends Vue {
	@Ref('category-modal')
	private readonly modal!: BModal;

	@Ref('import-modal')
	private readonly importModal!: BModal;

	@Ref('categoryTable')
	private readonly tableReference!: HTMLDivElement;

	protected isLoading = false;

	private offering: DB.OfferingModel[] = [];

	private language: DB.LanguageModel[] = [];

	private selectedRows: DB.ThemeCategoryModel[] = [];

	private table?: Tabulator;

	private placeHolders: { icon: string } = {
		icon: 'Choose a file',
	};

	private search = '';

	private id = '';

	private languageData = null;

	private offeringData = {} as DB.OfferingModel;

	private columnDefs: ColumnDefinitionExtended[] = [];

	private importCatalogData: ImportCatalogModel = {
		id: [],
		offering: {} as DB.OfferingModel,
	};

	private addCategoryData: AddCategoryModel = {
		name: '',
		languageid: null,
		offering: {} as DB.OfferingModel,
	};

	private isLoaded = false;

	private externalId = '';

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				title: 'Icon',
				field: 'icon',
				formatter: 'image',
				formatterParams: {
					height: 300,
					width: 300,
				},
			},
			{
				field: 'name',
				title: 'Name',
			},
			{
				title: 'Actions',
				formatter: (cell: CellComponentExtended<DB.ThemeCategoryModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'edit',
									eventName: 'editItem',
									className: 'fa-edit',
								},
								{
									id: 'delete',
									eventName: 'deleteItem',
									className: 'fa-times',
								},
							],
							eventBus,
						},
					});
					return instance;
				},
			},
		] as ColumnDefinitionExtended[];
	}

	protected mounted(): void {
		eventBus.on(
			'deleteItem',
			this.deleteCategory,
		);
		eventBus.on(
			'editItem',
			this.editCategory,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeUnmount(): void {
		eventBus.off(
			'deleteItem',
			this.deleteCategory,
		);
		eventBus.off(
			'editItem',
			this.editCategory,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.off(
			'rowSelectionChanged',
			this.onSelectionChanged,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData().then(() => this.table?.on(
			'rowSelectionChanged',
			this.onSelectionChanged,
		)).catch((err) => {
			this.$toastError(err.message);
		});
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
				selectable: true,
				// create a context menu to open row on a new tab
				rowContextMenu: [
					{
						label: 'Open in new tab',
						action: (e, row) => {
							const data = row.getData();
							this.$emit(
								'openInNewTab',
								data.id,
							);
						},
					},
				],
			},
		);
	}

	private async getData(): Promise<void> {
		const themeParameters = new URLSearchParams({
			orderby: 'serialnumber ASC',
			limit: '0',
		});

		const parameters = new URLSearchParams({
			limit: '0',
		});

		const data = {} as Record<string, string | number>;
		// if the Language data is not empty, set the locale to the selected language
		if (this.languageData && this.languageData !== null) {
			data.locale = this.languageData;
		}
		// if the offering data is not empty, set the groupid and typeid to the selected language
		if (Object.keys(this.offeringData).length !== 0) {
			data.groupid = this.offeringData.groupid;
			data.typeid = this.offeringData.typeid;
		}
		// append data to search params
		themeParameters.append(
			'where',
			JSON.stringify(data),
		);

		if (this.importCatalogData.id.length > 0) {
			themeParameters.append(
				'where',
				JSON.stringify({ id: this.importCatalogData.id }),
			);
		}
		this.table?.alert('Loading');
		try {
			const [templateData, offeringData, languageData] = await Promise.all([
				ThemeCategoryService.getAll(themeParameters),
				OfferingService.getAll(parameters),
				LanguageService.getAll(parameters),
			]);
			this.offering = offeringData;
			this.language = languageData;
			this.table?.setData(templateData);
		} catch (err: any) {
			this.$toastError(err.message);
		} finally {
			this.importModal.hide();
			this.table?.clearAlert();
		}
	}

	protected onSelectionChanged(): void {
		if (this.table) {
			this.selectedRows = this.table.getSelectedData();
		}
	}

	protected exportSelected(): void {
		const jsonString = JSON.stringify(this.selectedRows);
		const blob = new Blob(
			[jsonString],
			{ type: 'application/json' },
		);
		saveAs(
			blob,
			'Data.json',
		);
	}

	protected async exportAll(): Promise<void> {
		this.table?.alert('Loading...');
		try {
			const data = await ThemeCategoryService.getAll(new URLSearchParams({ limit: '0' }));
			const jsonString = JSON.stringify(data);
			const blob = new Blob(
				[jsonString],
				{ type: 'application/json' },
			);
			saveAs(
				blob,
				'Data.json',
			);
		} catch (error: any) {
			this.$toastError(error.message);
		} finally {
			this.table?.clearAlert();
		}
	}

	protected addCategory(): void {
		this.isLoaded = true;
		ThemeCategoryService.create(
			{
				name: this.addCategoryData.name,
				locale: this.addCategoryData.languageid?.toString(),
				typeid: this.addCategoryData.offering.typeid,
				groupid: this.addCategoryData.offering.groupid,
			},
		).then((res) => {
			this.$toastSuccess('Category Added');
			this.$emit(
				'routeCategory',
				res.id,
			);

			return undefined;
		})
			.finally(() => {
				this.isLoaded = false;
			})
			.catch((err) => {
				this.$toastError(err.message);
			});
	}

	protected async uploadFile(): Promise<void> {
		const options: PickerOptions = {
			fromSources: ['local_file_system'],
			onUploadDone: async (files: PickerResponse) => {
				this.placeHolders.icon = files.filesUploaded[0].filename;
				const data = (await ThemeCategoryService.import(
					{
						url: files.filesUploaded[0].url,
						typeid: this.importCatalogData.offering.typeid,
						groupid: this.importCatalogData.offering.groupid,
					},
				)) as DB.ThemeCategoryModel[];
				this.importCatalogData.id = data.map((item: DB.ThemeCategoryModel) => item.id);
				this.getData();
			},
		};
		this.$client.picker(options).open();
	}

	private editCategory(data: TabEvent<DB.ThemeCategoryModel>['editItem']): void {
		this.$emit(
			'routeCategory',
			data.id,
		);
	}

	private deleteCategory(data: TabEvent<DB.ThemeCategoryModel>['deleteItem']): void {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this file!',
			icon: 'warning',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-danger m-1',
				cancelButton: 'btn btn-secondary m-1',
			},
			confirmButtonText: 'Yes, delete it!',
			html: false,
		}).then((result) => {
			if (result.value) {
				this.table?.alert('Loading...');
				// eslint-disable-next-line promise/no-nesting
				ThemeCategoryService.delete(data.id).then(() => {
					this.$toastSuccess('Item Deleted');
					this.table?.deleteRow(data.id);
					return null;
				}).finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$toastError(err.message);
				});
			}
			return undefined;
		}).catch((err) => {
			this.$toastError(err.message);
		});
	}
}

export default toNative(CategoryTable);
